import { DateTime } from 'luxon';
import { MoreHorizontal } from 'lucide-react';
import type { ReactNode } from 'react';
import { Link } from 'react-router';
import { CampaignStatusBadge } from '@/components/campaigns/campaign-status-badge';
import { formatCurrency, formatNumber } from '@/components/metrics/formatters';
import { Skeleton } from '@/components/ui/skeleton';
import type { CampaignDto } from '@/generated/korza-api-client/types.gen';
import { useTenant } from '@/hooks/use-tenant';
import { useI18nContext } from '@/i18n/i18n-react';
import { getCampaignCardFacts } from '@/lib/campaign-list';
import { mobileLayer } from '@/lib/mobile-layer';
import { cn } from '@/lib/utils';

function Fact({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="min-w-0">
      <dt className="text-xs text-muted-foreground">{label}</dt>
      <dd className="truncate text-sm font-semibold tabular-nums">
        {children}
      </dd>
    </div>
  );
}

/**
 * Campaign of the mobile list (PUSH-01 AC 2): status, send date and time,
 * name and notification title, then orders/revenue/ticket for a sent campaign
 * with analytics, or the audience for a scheduled one or a draft. The card
 * links to the detail; ⋯ opens the actions (PUSH-04 AC 2).
 */
export function CampaignCard({
  campaign,
  currency,
  analyticsEnabled,
  locale,
  onOpenActions,
}: {
  campaign: CampaignDto;
  /** `undefined` while the store currency loads: money shows a skeleton. */
  currency: string | undefined;
  analyticsEnabled: boolean;
  locale: string;
  /** Receives the ⋯ button, so the sheet can return focus to it. */
  onOpenActions(trigger: HTMLButtonElement): void;
}) {
  const { LL } = useI18nContext();
  const tenant = useTenant();
  const facts = getCampaignCardFacts(campaign, analyticsEnabled);

  const money = (value: number | null) => {
    if (value === null) return '—';
    if (currency === undefined) {
      return (
        <Skeleton data-testid="currency-skeleton" className="mt-0.5 h-4 w-16" />
      );
    }
    return formatCurrency(value, locale, currency);
  };

  return (
    <article
      className={cn(
        'relative rounded-lg border bg-card text-card-foreground',
        mobileLayer.card
      )}
    >
      <Link
        to={`/${tenant.slug}/campaigns/${campaign.id}`}
        className="flex flex-col gap-2 rounded-[inherit] p-4 pr-14 outline-none transition-colors active:bg-accent focus-visible:ring-2 focus-visible:ring-ring"
      >
        <span className="flex items-center gap-2">
          <CampaignStatusBadge status={campaign.status} />
          <time
            dateTime={campaign.launchDateTime}
            className="truncate text-xs text-muted-foreground"
          >
            {DateTime.fromISO(campaign.launchDateTime)
              .setLocale(locale)
              .toLocaleString(DateTime.DATETIME_MED)}
          </time>
        </span>
        <span className="min-w-0">
          <span className="block truncate font-semibold">{campaign.name}</span>
          <span className="block truncate text-sm text-muted-foreground">
            {campaign.notificationTitle}
          </span>
        </span>
        {facts.kind === 'results' && (
          <dl className="grid grid-cols-3 gap-2 border-t pt-2">
            <Fact label={LL.campaigns.card.orders()}>
              {formatNumber(facts.paidOrderCount, locale)}
            </Fact>
            <Fact label={LL.campaigns.card.revenue()}>
              {money(facts.revenue)}
            </Fact>
            <Fact label={LL.campaigns.card.ticket()}>
              {money(facts.ticket)}
            </Fact>
          </dl>
        )}
        {facts.kind === 'audience' && (
          <dl className="border-t pt-2">
            <Fact label={LL.campaigns.card.audience()}>
              <span className="font-medium">
                {facts.audienceName ?? LL.campaigns.card.allUsers()}
              </span>
            </Fact>
          </dl>
        )}
      </Link>
      <button
        type="button"
        aria-label={LL.campaigns.card.moreActions({ name: campaign.name })}
        onClick={(event) => onOpenActions(event.currentTarget)}
        className="absolute right-1 top-1 inline-flex min-h-touch min-w-touch items-center justify-center rounded-full text-muted-foreground outline-none transition-colors hover:bg-accent focus-visible:ring-2 focus-visible:ring-ring"
      >
        <MoreHorizontal className="size-5" aria-hidden />
      </button>
    </article>
  );
}
